import { useState, useRef, useEffect, useCallback } from "react";
import { defaultCandidateEngine } from "../state/store";
import { usePaneStore, useVerificationStoreApi } from "../state/store-context";
import { useShallow } from "zustand/react/shallow";
import { useContentValidateRules } from "../context/ContentValidateContext";
import { useLookupSnapshots, useJobConstants } from "../context/LookupSnapshotContext";
import { pickActiveEntry } from "../hooks/useContentValidateBundle";
import { normalizeKeyedValue } from "../lib/normalize-keyed-value";
import { useFieldFocusAndSelect } from "../hooks/useFieldFocusAndSelect";
import { resolveEffectiveThreshold, resolveFieldHighlight, interpolateMessage } from "@capture/shared";
import { toast } from "@/components/Toaster";
import { api } from "@/lib/api-client";
import { CandidateArbitration } from "./CandidateArbitration";
import { SuspectCharacters } from "./SuspectCharacters";
import { CheckAmountAid } from "./CheckAmountAid";

interface FieldInputProps {
  fieldIndex: number;
}

export function FieldInput({ fieldIndex }: FieldInputProps) {
  const field = usePaneStore(
    useShallow((s) => s.fields[fieldIndex]),
  );
  const isActive = usePaneStore(
    (s) => s.activeFieldIndex === fieldIndex,
  );
  const setFieldValue = usePaneStore((s) => s.setFieldValue);
  const setActiveField = usePaneStore((s) => s.setActiveField);
  // Handwriting Phase 3 — which candidate the Ctrl+E toggle has active for
  // this field; absent = the diag's winning engine (the persisted value).
  const activeEngineOverride = usePaneStore(
    (s) => s.activeEngineByFieldId[s.fields[fieldIndex]?.id ?? ""],
  );
  const advanceField = usePaneStore((s) => s.advanceField);
  const applyValidationResults = usePaneStore((s) => s.applyValidationResults);
  // SP3 §4 — {field}-token interpolation must snapshot THIS pane's fields, not
  // the module default (empty inside a pane). Falls back to the default store
  // on the standalone /verify route where no provider is mounted.
  const storeApi = useVerificationStoreApi();
  const contentValidateRules = useContentValidateRules();
  const lookupSnapshots = useLookupSnapshots();
  const jobConstants = useJobConstants();
  const verifyThreshold = usePaneStore((s) => s.verifyThreshold);
  // S-5 Task 7 review round 2, item 2 — "Retry check" needs the doc id to
  // hit POST /documents/:id/webhook-checks/:ruleId/refire.
  const documentId = usePaneStore((s) => s.documentId);

  // R-142 — ref now covers <input> AND <select> branches (dropdown fields).
  // Both elements have .focus(); only <input> has .select(), so guard below.
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null);
  const setInputRef = useCallback((el: HTMLInputElement | HTMLSelectElement | null) => {
    inputRef.current = el;
  }, []);
  const [localValue, setLocalValue] = useState(field?.value ?? "");

  // Sync local value when field loads or changes externally
  useEffect(() => {
    if (field) setLocalValue(field.value);
  }, [field?.value]);

  // Focus + select-all on activation and on an E-072 error-stop request.
  useFieldFocusAndSelect(fieldIndex, isActive, inputRef);

  const handleBlur = useCallback(() => {
    if (!field) return;

    // Apply force uppercase on commit, then E-021 per-field input-format
    // normalization (e.g. separatorless MMDDYYYY date → canonical ISO). No
    // keyStandard / unparseable input leaves the value untouched (back-compat).
    const uppercased = field.forceUppercase ? localValue.toUpperCase() : localValue;
    const committedValue = normalizeKeyedValue(uppercased, field);
    if (committedValue !== localValue) setLocalValue(committedValue);

    // F-045 — did the operator actually re-key this field on this blur? Used to
    // decide whether to fire the keying-time failure toast (we don't want to
    // re-toast every time the cursor leaves an already-failing field it didn't
    // touch — the persistent inline error already covers that).
    const valueChanged = committedValue !== field.value;
    if (valueChanged) {
      setFieldValue(field.id, committedValue);
    }

    // R-239 Plan B (B3.T2a.2) — evaluate this field's content `validate` rule
    // CLIENT-SIDE on the committed value (design §9 "client instant"): pure,
    // synchronous, no server hop, no debounce race. The result drives the
    // field's validationStatus/message/overrideBehavior so the blur-trap
    // (B3.T2b) and Ctrl+O override (B4) act on a fresh status. Fail-open: a
    // field with no content rule is left untouched.
    // F-037 — the bundle carries ALL of this field's bindings (derived first,
    // then authored — walk order). Evaluate them in order and report the FIRST
    // failure, true parity with the server pass's first-failure-per-field.
    // The old one-entry bundle silently dropped authored rules behind derived
    // checks, so an authored compare never fired on blur.
    const contentRules = contentValidateRules[field.name];
    // F-023 — substitute {field} tokens in the failure message with the
    // current field values so the operator sees the offending data. Snapshot
    // values by name (incl. this field's just-committed value). Task 12b —
    // this is ALSO the EvalContext for gating an authored branch-IF, so a
    // condition referencing another field reads the same snapshot the
    // message interpolation uses. Built only when there are content rules to
    // evaluate — a field with none skips the O(fields) snapshot entirely.
    const valuesByName: Record<string, string> | undefined = contentRules
      ? Object.fromEntries(
          storeApi.getState().fields.map((f) => [f.name, f.id === field.id ? committedValue : f.value]),
        )
      : undefined;
    // P1 — confidence slice mirrors valuesByName's build (same store fields),
    // so a `confidence`-source branch-IF resolves at blur exactly as at accept.
    const confidenceByName: Record<string, number> | undefined = contentRules
      ? Object.fromEntries(
          storeApi.getState().fields
            .filter((f) => f.ocrConfidence != null)
            .map((f) => [f.name, f.ocrConfidence as number]),
        )
      : undefined;
    const picked = contentRules
      ? pickActiveEntry(contentRules, committedValue, {
          fields: valuesByName!,
          fieldConfidence: confidenceByName,
          lookupData: lookupSnapshots,
          constants: jobConstants,
        })
      : null;
    if (picked) {
      const { entry: winner, valid, message } = picked;
      const overrideBehavior = winner.overrideBehavior;
      const displayMessage = interpolateMessage(message, valuesByName!);
      applyValidationResults({
        [field.id]: {
          valid,
          message: displayMessage,
          severity: winner.severity === "warn" ? "warn" : "error",
          ...(overrideBehavior !== undefined ? { overrideBehavior } : {}),
        },
      });

      // F-045 (c) — fire a brief toast on a rule failure at keying time, in
      // addition to the persistent inline error + red fill. Only when the
      // operator actually re-keyed the field this blur (valueChanged), so an
      // unchanged already-failing field isn't re-toasted on every cursor exit.
      if (!valid && valueChanged) {
        toast.error(displayMessage || `${field.label} failed validation.`);
      }

      // F-077 — no blur-trap: navigation between fields is never contested.
      // A failing field keeps its fail status/outline; enforcement moved
      // entirely to the submit gate (formReadyToSubmit effect + accept path).
    }

  }, [field, fieldIndex, localValue, setFieldValue, contentValidateRules, lookupSnapshots, applyValidationResults]);

  const handleFocus = useCallback(() => {
    setActiveField(fieldIndex);
  }, [fieldIndex, setActiveField]);

  if (!field) return null;

  // S-5 Task 7 review round 2, item 2 — the operator's escape hatch: force
  // a fresh dial via the existing refire route (Task 4,
  // apps/api/src/routes/documents/webhook-checks.ts), which was built but
  // had no UI wired to it. Not a hook (no state/effect needed) — a plain
  // function is enough for a fire-and-forget click handler. Errors are
  // swallowed with a toast rather than thrown: the WS webhook_check_update
  // event (or the next accept attempt) is the actual source of truth for
  // whether the retry succeeded, not this POST's response.
  const webhookCheckRuleId = field.webhookCheckRuleId;
  function handleRetryCheck() {
    if (!documentId || !webhookCheckRuleId) return;
    void api
      .post(`/documents/${documentId}/webhook-checks/${webhookCheckRuleId}/refire`)
      .catch(() => toast.error("Retry check failed — try again."));
  }

  // 1:1 with the status name; "pending" renders unstyled.
  const statusClass =
    field.validationStatus === "pending" ? "" : `status-${field.validationStatus}`;

  // R-32 P2: per-field → per-JT → 0.95 default (same resolver the store
  // uses for tab-skip, so highlight + navigation agree by construction).
  const effectiveThreshold = resolveEffectiveThreshold(field.verifyThreshold, verifyThreshold);

  // R-32 P2: 3-state highlight — blank (good/resolved/skipped) / yellow
  // (recognized but < threshold, verify) / red (not recognized or failed).
  const highlight = resolveFieldHighlight(field, effectiveThreshold);
  const highlightClass =
    highlight === "yellow" ? "field-hl-yellow" : highlight === "red" ? "field-hl-red" : "";

  // R-239 Task 7: two-tier border outline — red (rule fail) / amber (low OCR
  // confidence, rule-passing). Fail wins: if a field both fails a rule AND has
  // low confidence, the red border takes priority (the !isFail guard handles this).
  const isFail = field.validationStatus === "fail";
  // F-023 — a standing warning gets a visible amber outline (never gates
  // navigation or accept; it informs and requires a one-time ack at accept).
  const isWarn = !isFail && field.validationStatus === "warn";
  const isLowConf =
    !isFail &&
    !isWarn &&
    field.ocrConfidence !== null &&
    field.ocrConfidence < effectiveThreshold;
  const outlineClass = isFail
    ? "field-input--fail"
    : isWarn
      ? "field-input--warn"
      : isLowConf
        ? "field-input--lowconf"
        : "";

  // F-045 — a field that was KEYED and still fails its rule must look clearly
  // distinct from an un-keyed failing field (a stronger background tint), so
  // the operator can see at a glance WHY the cursor stays trapped. Ties to the
  // same --color-error system as the outline for consistency.
  const failEditedClass = isFail && field.isDirty ? "field-input--fail-edited" : "";

  const isAutoVerified =
    field.ocrConfidence !== null &&
    field.ocrConfidence >= effectiveThreshold &&
    !field.isDirty &&
    field.validationStatus !== "fail" &&
    !(field.required && field.value === "");

  // Shared by the <select> and <input> field renderers — keep them identical.
  const inputClassName = `field-input ${highlightClass} ${isAutoVerified ? "auto-verified-input" : ""} ${field.readOnly ? "field-input--derived" : ""} ${outlineClass} ${failEditedClass}`;

  return (
    <div
      className={[
        "field-row",
        // R-127 / Phase 1.5 WU-4 — paired-highlight class on the active row.
        // Mirrors the canvas's active-bbox highlight (`#ea580c` in
        // `canvas/renderer.ts`) so the operator sees the input + the form
        // image zone as one visual pair. `.field-active` is preserved for
        // legacy styles; `.field-hl-current` is the canonical R-127 hook.
        isActive ? "field-active field-hl-current" : "",
        statusClass,
        isAutoVerified ? "field-auto-verified" : "",
        // Keyed OR explicitly acknowledged (R-144) + no failures = done (green);
        // still needs work = attention (yellow).
        (field.isDirty || field.isAcknowledged) &&
        field.validationStatus !== "fail" &&
        !(field.required && !field.value)
          ? "field-done"
          : field.needsAttention && !isActive
            ? "field-needs-attention"
            : "",
      ].filter(Boolean).join(" ")}
    >
      <label className="field-label" title={field.name}>
        {isAutoVerified && (
          <span
            className="field-auto-check"
            title={`Auto-verified (${Math.round(field.ocrConfidence! * 100)}% confidence)`}
          >
            &#x2713;
          </span>
        )}
        {field.label}
        {field.required && <span className="field-required">*</span>}
        {field.isHandwriting && (
          <span
            className="handwriting-indicator"
            title="Handwritten source — OCR confidence may be lower"
            aria-label="Handwritten field"
          >
            &#x270D;
          </span>
        )}
        {field.autoCorrected && (
          <span
            className="auto-corrected-indicator"
            title={`Auto-corrected from '${field.preCorrectionValue}' by ${field.correctionSource ?? "intelligence model"}`}
            aria-label="Auto-corrected field"
          >
            &#x2728;
          </span>
        )}
        {field.isVerified && (
          // F-046 — surface the upstream human sign-off so a supervisor can
          // SEE that a field was already verified rather than inferring it.
          // Same label-indicator idiom as ✍/✨ above.
          <span
            className="field-upstream-verified"
            title={`Verified upstream${field.verifiedAt ? ` on ${new Date(field.verifiedAt).toLocaleString()}` : ""}`}
            aria-label="Verified at an earlier step"
          >
            &#x2713;&#x2713;
          </span>
        )}
        {field.helpText && (
          // WU-1.5 — authored helpText as an inline info glyph + tooltip.
          // Follows the existing label-indicator idiom (✍/✨ above) so the
          // row height never shifts when the hint is absent.
          <span
            className="field-help-indicator"
            title={field.helpText}
            aria-label={`Field help: ${field.helpText}`}
          >
            &#x24D8;
          </span>
        )}
      </label>
      <div className="field-input-wrap">
        {field.dataType === "boolean" ? (
          <label className={`field-input-toggle ${highlightClass} ${isAutoVerified ? "auto-verified-input" : ""} ${field.readOnly ? "field-input--derived" : ""}`}>
            <input
              ref={setInputRef}
              type="checkbox"
              disabled={field.readOnly}
              checked={localValue === "true"}
              onChange={(e) => {
                const newValue = e.target.checked ? "true" : "false";
                setLocalValue(newValue);
                setFieldValue(field.id, newValue);
              }}
              onFocus={handleFocus}
              onKeyDown={(e) => {
                if (e.key === "1" || e.key === "y" || e.key === "Y") {
                  e.preventDefault();
                  setLocalValue("true");
                  setFieldValue(field.id, "true");
                } else if (e.key === "0" || e.key === "n" || e.key === "N") {
                  e.preventDefault();
                  setLocalValue("false");
                  setFieldValue(field.id, "false");
                } else if (e.key === "Tab" || e.key === "Enter") {
                  e.preventDefault();
                  advanceField(e.shiftKey ? "prev" : "next");
                }
              }}
              data-field-index={fieldIndex}
            />
            <span className="toggle-label">{localValue === "true" ? "Yes" : "No"}</span>
          </label>
        ) : field.dataType === "dropdown" && field.dropdownOptions ? (
          // R-142 — render a real <select> for dropdown fields. Without this
          // branch the operator gets a freeform text input and must type one
          // of the valid options exactly, with no hint of what they are.
          <select
            ref={setInputRef}
            disabled={field.readOnly}
            className={inputClassName}
            value={localValue}
            onChange={(e) => {
              setLocalValue(e.target.value);
              setFieldValue(field.id, e.target.value);
            }}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                advanceField("next");
              }
            }}
            data-field-index={fieldIndex}
          >
            <option value="">— Select —</option>
            {field.dropdownOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            ref={setInputRef}
            type="text"
            disabled={field.readOnly}
            className={inputClassName}
            value={localValue}
            onChange={(e) => {
              let val = field.forceUppercase ? e.target.value.toUpperCase() : e.target.value;
              if (field.maxLength && val.length > field.maxLength) val = val.slice(0, field.maxLength);
              setLocalValue(val);
            }}
            maxLength={field.maxLength ?? undefined}
            // WU-1.5 — authored placeholder. Rendered with the native
            // placeholder idiom (italic + muted via CSS) so it stays clearly
            // distinct from real values — see the F-089 caution: verified
            // VALUES render at normal color precisely because muted-in-input
            // reads as placeholder.
            placeholder={field.placeholder ?? undefined}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onKeyDown={(e) => {
              // Handwriting Phase 3 — Escape restores the last COMMITTED value
              // (the keyer's bail-out after starting a type-over) and
              // re-selects so the next keystroke replaces again.
              if (e.key === "Escape") {
                e.preventDefault();
                setLocalValue(field.value);
                e.currentTarget.select();
              }
            }}
            data-field-index={fieldIndex}
          />
        )}
        {field.validationStatus === "fail" && field.validationMessage && (
          <span className="field-error" title={field.validationMessage}>!</span>
        )}
        {field.isOverridden && (
          <span className="field-override-icon" title={`Override: ${field.overrideReason}`}>&#x26A1;</span>
        )}
        {field.ocrConfidence !== null && !field.isDirty && (
          <span className={`field-confidence ${highlightClass}`}>
            {Math.round(field.ocrConfidence * 100)}
          </span>
        )}
        {/* S-5 Task 7 — any-timing webhook-check chip (design §5). Reads
            field.webhookCheckStatus, driven live by WS webhook_check_update
            events via applyWebhookCheckUpdate — INDEPENDENT of
            validationStatus (never gates the fail/warn outline above). A
            plain <span>, never a button/no tabIndex — must never enter the
            keyboard-first tab order or steal focus from the active input. */}
        {field.webhookCheckStatus === "pending" && (
          <span
            className="webhook-check-chip webhook-check-chip--pending"
            data-testid="webhook-check-pending"
            title={
              field.webhookCheckRuleName
                ? `Checking: ${field.webhookCheckRuleName}`
                : "Check pending"
            }
          >
            <span className="webhook-check-chip__spinner" aria-hidden="true" />
            {field.webhookCheckRuleName ?? "Checking…"}
          </span>
        )}
        {field.webhookCheckStatus === "failed" && (
          <span
            className="webhook-check-chip webhook-check-chip--failed"
            data-testid="webhook-check-failed"
            title={field.webhookCheckMessage ?? undefined}
          >
            {field.webhookCheckRuleName ?? "Check failed"}
          </span>
        )}
        {field.webhookCheckStatus === "unresolved" && (
          <span
            className="webhook-check-chip webhook-check-chip--unresolved"
            data-testid="webhook-check-unresolved"
            title={field.webhookCheckMessage ?? undefined}
          >
            {field.webhookCheckRuleName ?? "Check unresolved"}
          </span>
        )}
        {/* S-5 Task 7 review round 2, item 2 — "Retry check": the
            operator's escape hatch onto the existing (previously
            unreachable) refire route. Shown for 'pending' (stuck/slow) or
            'unresolved' (needs a fresh dial); NOT for 'failed' (the rule
            actually answered "no" — re-dialing won't change that, the
            operator's move is override/escalate) or absent (nothing to
            retry). A real <button> — keyboard-reachable (default tabIndex
            0) — but never auto-focused; only a user click/Enter/Space
            activates it. */}
        {(field.webhookCheckStatus === "pending" || field.webhookCheckStatus === "unresolved") &&
          field.webhookCheckRuleId && (
            <button
              type="button"
              className="webhook-check-retry-btn"
              data-testid="webhook-check-retry"
              onClick={handleRetryCheck}
              title="Force a fresh check now"
            >
              Retry
            </button>
          )}
      </div>
      {/* Handwriting Phase 3 — engine-disagreement arbitration panel. Mounted
          ONLY when this field is active AND both candidates exist (fusion
          populates candidates solely on disagreement) — gate-off environments
          and agreement fields render nothing here, byte-identical to pre-WU. */}
      {isActive && field.handwritingCandidates && (
        <CandidateArbitration
          candidates={field.handwritingCandidates}
          activeEngine={activeEngineOverride ?? defaultCandidateEngine(field.handwritingCandidates)}
        />
      )}
      {/* ADR-013 — suspect-character readout for the agreement-case and
          single-engine fields candidate-diff cannot cover. Diff wins the
          visual hierarchy: when an arbitration panel is showing, this does
          not mount. SuspectCharacters itself renders null without
          sub-threshold spans, so pre-ADR-013 rows are byte-identical. */}
      {isActive && !field.handwritingCandidates && (
        <SuspectCharacters charConfidences={field.charConfidences ?? null} />
      )}
      {/* Lane C3 — CAR/LAR check-amount keying aid. Mounts ONLY when this field
          is active AND the courtesy/legal readings did not cross-confirm
          (mapper yields candidates only on agreement:"none"); a confirmed
          amount pre-fills ocrValue and yields no candidates, so this renders
          nothing. Click-to-fill stages the value (setLocalValue + setFieldValue)
          exactly like an operator keystroke — never auto-accepts. */}
      {isActive && field.checkAmountCandidates && (
        <CheckAmountAid
          candidates={field.checkAmountCandidates}
          onFill={(value) => {
            setLocalValue(value);
            setFieldValue(field.id, value);
          }}
        />
      )}
      {/* F-045 — persistent, PROMINENT inline error: render the authored
          message text (not just a "!" tooltip) so the operator can read WHY
          the field is trapped without hovering. Stays until the rule passes. */}
      {field.validationStatus === "fail" && field.validationMessage && (
        <p className="field-error-message" role="alert">
          {field.validationMessage}
        </p>
      )}
    </div>
  );
}
